import React, { useEffect, useState } from "react";
import { Box, IconButton, Modal, TextField, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../../theme";
import { ToastContainer, toast } from "react-toastify";

import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTheme } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import "./style.css";
import {
  fetchAllClientsData,
  updateClientsData,
  insetClientData,
  deleteClientsData,
} from "../../../../services/apiService";
import Topbar from "../../../../scenes/global/Topbar";

const GyanniClients = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [role, setRole] = useState(null);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // update
  const [currentClient, setCurrentClient] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const initialData = await fetchAllClientsData({});

        const limit = initialData.details.total;
        const dataWithLimit = { limit: limit };
        const responseData = await fetchAllClientsData(dataWithLimit);

        const formattedData = responseData.details.values.map(
          (client, index) => ({
            id: client.id,
            name: client.name,
            code: client.code,
            description: client.description,
            keyperson: client.keyperson,
            keymobile: client.keymobile,
            keyemail: client.keyemail,
            spocname: client.spocname,
            spocemail: client.spocemail,
            spocnumber: client.spocnumber,
            LicenseStartDate: client.lecensestartdate,
            LicenseEndDate: client.licenseenddate,
          })
        );

        setClients(formattedData);
      } catch (error) {
        console.error("Error fetching clients data:", error);
        setError("Failed to fetch clients data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const [filterModel, setFilterModel] = useState({
    items: [],
  });

  const handleFilterChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const handleSearchChange = (columnField, value) => {
    setFilterModel((prevState) => {
      const newItems = [...prevState.items];
      const index = newItems.findIndex(
        (item) => item.columnField === columnField
      );
      if (index !== -1) {
        newItems[index] = { ...newItems[index], value };
      } else {
        newItems.push({ columnField, operatorValue: "contains", value });
      }
      return { items: newItems };
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleEdit = (client) => {
    setCurrentClient(client);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setCurrentClient(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateData = async (e) => {
    e.preventDefault();
    try {
      const updatedClient = await updateClientsData(currentClient);
      if (updatedClient.success) {
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.id === currentClient.id ? { ...currentClient } : client
          )
        );
        toast.success("Client updated successfully.");
        handleCloseModal();
      } else {
        toast.error(updatedClient.message || "Failed to update client.");
      }
    } catch (error) {
      toast.error("An error occurred while updating client.");
    }
  };

  const [insertData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    keyperson: "",
    keyemail: "",
    keymobile: "",
    spocname: "",
    spocemail: "",
    spocnumber: "",
    baseflag: "0",
    lecensestartdate: "",
    licenseenddate: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...insertData, [name]: value });
  };

  const handleInsertData = async () => {
    try {
      const response = await insetClientData(insertData);
      if (response.success) {
        toast.success("Client Added Successfully!");
      } else {
        toast.error(
          response?.message || "Client insertion failed. Please try again."
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "An error occurred. Please try again."
      );
    }
  };

  const handleDelete = async (clientId) => {
    try {
      const response = await deleteClientsData(clientId);
      if (response.success) {
        toast.success("Client deleted successfully!");
      } else {
        toast.error(response?.message || "Failed to delete client.");
      }
    } catch (err) {
      console.error("Error deleting client:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to delete client.";
      toast.error(errorMessage);
    }
  };

  const columns = [
    // { field: "id", headerName: "ID" },
    {
      field: "name",
      headerName: "Client Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "code",
      headerName: "Client Code",
      type: "text",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },
    {
      field: "keyperson",
      headerName: "Key Person",
      flex: 1,
    },
    {
      field: "keymobile",
      headerName: "Key Mobile",
      flex: 1,
    },
    {
      field: "keyemail",
      headerName: "Key Email",
      flex: 1,
    },
    {
      field: "spocname",
      headerName: "Spoc Name",
      flex: 1,
    },
    {
      field: "spocemail",
      headerName: "Spoc Email",
      flex: 1,
    },
    {
      field: "spocnumber",
      headerName: "Spoc Number",
      flex: 1,
    },
    {
      field: "LicenseStartDate",
      headerName: "License Start Date",
      flex: 1,
    },
    {
      field: "LicenseEndDate",
      headerName: "License End Date",
      flex: 1,
    },

    {
      field: "action",
      headerName: "Action",
      renderCell: ({ row }) => (
        <div style={{ display: "flex" }}>
          <IconButton
            onClick={() => handleEdit(row)}
            style={{ color: "#6870fa", fontSize: "12px" }}
          >
            <BorderColorIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(row.id)}
            style={{ color: "red", fontSize: "12px" }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mt-4">
        <Topbar />
      </div>
      <Box m="20px" style={{ backgroundColor: colors.primary[400] }}>
        <div className="p-4 flex items-center justify-between">
          <h2 className="font-semibold text-xl">Client Details</h2>
          <div
            style={{ backgroundColor: colors.blueAccent[500] }}
            className="text-white px-4 py-2 rounded-sm"
          >
            <button onClick={openModal} className="font-medium">
              {" "}
              <AddIcon /> Add
            </button>
          </div>
        </div>

        <Box
          m="0 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        >
          <DataGrid
            checkboxSelection
            rows={clients}
            columns={columns}
            pageSize={pageSize}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            pagination
            loading={loading}
            filterModel={filterModel}
            onFilterModelChange={handleFilterChange}
          />
        </Box>

        {/* Modal for editing client */}
        <div
          className={`fixed inset-0 bg-gray-500 bg-opacity-50 z-50 transition-opacity ${
            isEditModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex justify-center items-center min-h-screen">
            <div className="bg-white p-6 rounded-lg shadow-lg w-6/12">
              <h6 className="text-center font-semibold mb-4">
                Edit Client Details
              </h6>

              <form
                className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6"
                onSubmit={handleUpdateData}
              >
                {/* Client Name */}
                <div className="mb-3">
                  <TextField
                    label="Client Name"
                    name="name"
                    value={currentClient?.name}
                    onChange={handleChange}
                    fullWidth
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                </div>

                {/* Client Code */}
                <div className="mb-3">
                  <TextField
                    label="Client Code"
                    name="code"
                    value={currentClient?.code}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Description */}
                <div className="mb-3">
                  <TextField
                    label="Description"
                    name="description"
                    value={currentClient?.description}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Key Person Name */}
                <div className="mb-3">
                  <TextField
                    label="Key Person"
                    name="keyperson"
                    value={currentClient?.keyperson}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Key Person Mobile */}
                <div className="mb-3">
                  <TextField
                    label="Key Mobile"
                    name="keymobile"
                    value={currentClient?.keymobile}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Key Person Email */}
                <div className="mb-3">
                  <TextField
                    label="Key Email"
                    name="keyemail" // Ensure consistent field name
                    value={currentClient?.keyemail}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* baseflag */}
                <div className="mb-3">
                  <TextField
                    label="baseflag"
                    name="baseflag"
                    value="0"
                    fullWidth
                    disabled
                  />
                </div>

                {/* Spoc Person Name */}
                <div className="mb-3">
                  <TextField
                    label="Spoc Person"
                    name="spocname"
                    value={currentClient?.spocname}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Spoc Person Mobile */}
                <div className="mb-3">
                  <TextField
                    label="Spoc Mobile"
                    name="spocnumber"
                    value={currentClient?.spocnumber}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                {/* Spoc Person Email */}
                <div className="mb-3">
                  <TextField
                    label="Spoc Email"
                    name="spocemail" // Ensure consistent field name
                    value={currentClient?.spocemail}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>

                <div className="flex items-center justify-between space-x-4">
                  {/* License Start Date */}
                  <div className="flex-1">
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      License Start Date
                    </label>
                    <div className="relative flex items-center">
                      <input
                        disabled
                        name="lecensestartdate"
                        type="text" // Use date type for date input
                        value={currentClient?.LicenseStartDate}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* License End Date */}
                  <div className="flex-1">
                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      License End Date
                    </label>
                    <div className="relative flex items-center">
                      <input
                        disabled
                        name="licenseenddate"
                        type="text"
                        value={currentClient?.LicenseEndDate}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit and Close Buttons */}
                <div className="col-span-2 flex justify-between space-x-4">
                  <button
                    type="submit" // Use type="submit" for form submission
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  >
                    Update
                  </button>
                  <button
                    type="button" // Prevent form submission for close button
                    onClick={handleCloseModal}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-400 focus:outline-none"
                  >
                    X
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* // add client modal pop up  */}
        <div
          className={`fixed inset-0 bg-gray-500 bg-opacity-50 z-50 transition-opacity ${
            isModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex justify-center items-center min-h-screen">
            <div className="bg-white p-6 rounded-lg shadow-lg w-6/12 ">
              <h2 className="text-2xl text-center font-semibold mb-4">
                Add Client
              </h2>

              <form className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Client Name */}
                <div className="mb-3">
                  <label
                    htmlFor="name"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Client Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    onChange={handleInputChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                  />
                </div>

                {/* Client Code */}
                <div className="mb-3">
                  <label
                    htmlFor="code"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Client Code
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    onChange={handleInputChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                  />
                </div>

                {/* Description */}
                <div className="mb-3">
                  <label
                    htmlFor="description"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    id="description"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    name="description"
                    onChange={handleInputChange}
                  />
                </div>

                {/* Key Person Name */}
                <div className="mb-3">
                  <label
                    htmlFor="keyperson"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Key Person Name
                  </label>
                  <input
                    type="text"
                    id="keyperson"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    name="keyperson"
                    onChange={handleInputChange}
                  />
                </div>

                {/* Key Person Email */}
                <div className="mb-3">
                  <label
                    htmlFor="keyPersonEmail"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Key Person Email
                  </label>
                  <input
                    type="text"
                    id="keyPersonEmail"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    onChange={handleInputChange}
                    name="keyemail"
                  />
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="keyPersonEmail"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Key Person Mobile Number
                  </label>
                  <input
                    type="text"
                    id="keyPersonEmail"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    onChange={handleInputChange}
                    name="keymobile"
                  />
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="keyPersonEmail"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    SPOC Name
                  </label>
                  <input
                    type="text"
                    id="keyPersonEmail"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    onChange={handleInputChange}
                    name="spocname"
                  />
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="keyPersonEmail"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    SPOC Email
                  </label>
                  <input
                    type="text"
                    id="keyPersonEmail"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    onChange={handleInputChange}
                    name="spocemail"
                  />
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="keyPersonEmail"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    SPOC Mobile Number
                  </label>
                  <input
                    type="text"
                    id="keyPersonEmail"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                    onChange={handleInputChange}
                    name="spocnumber"
                  />
                </div>

                <div className="flex items-center justify-between space-x-4">
                  {/* License Start Date */}
                  <div className="flex-1">
                    <label
                      htmlFor="datepicker-range-start"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      License Start Date
                    </label>
                    <div className="relative flex items-center">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500 dark:text-gray-400"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                        </svg>
                      </div>
                      <input
                        id="datepicker-range-start"
                        name="lecensestartdate"
                        type="date"
                        onChange={handleInputChange}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Select start date"
                      />
                    </div>
                  </div>

                  <span className="text-gray-500">to</span>

                  {/* License End Date */}
                  <div className="flex-1">
                    <label
                      htmlFor="datepicker-range-end"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      License End Date
                    </label>
                    <div className="relative flex items-center">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500 dark:text-gray-400"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                        </svg>
                      </div>
                      <input
                        id="datepicker-range-end"
                        name="licenseenddate"
                        type="date"
                        onChange={handleInputChange}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Select end date"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit and Close Buttons */}
                <div className="col-span-2 flex justify-between space-x-4">
                  <button
                    onClick={handleInsertData}
                    type="submit"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  >
                    Submit
                  </button>
                  <button
                    onClick={closeModal}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-400 focus:outline-none"
                  >
                    X
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <ToastContainer />
      </Box>
    </>
  );
};

export default GyanniClients;
